import React from 'react';
import { ModalProps } from '../../../types/modal';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    CommercialType,
    ActivityType,
    BoatType,
    ExperienceType,
    PropertyType,
    VehicleType,
} from '../../../enums/experience';
import UserRole from '../../../../shared/AuthRoles';
import { useCreateExperienceMutation } from '../../../../app/Slices/SevenDayApiSlice';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    DialogActions,
    Fade,
    Stack,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { MODULE_TYPE_CHIPS } from '../../../pages/Experience Pages/Guest/ViewAllExperiencesPage';
import FormSelect from '../../../shared/FormSelect';
import toast from '../../../shared/Toast';
import { CreateExperience } from '../../../../app/Slices/SevenDayApiTypes';

const ListingModal = ({ open, onClose }: ModalProps) => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    const methods = useForm<CreateExperience>({
        resolver: zodResolver(
            z.discriminatedUnion('type', [
                z.object({
                    type: z.literal(ExperienceType.Property),
                    details: z.object({
                        subType: z.nativeEnum(PropertyType),
                    }),
                }),
                z.object({
                    type: z.literal(ExperienceType.Vehicle),
                    details: z.object({
                        subType: z.nativeEnum(VehicleType),
                    }),
                }),
                z.object({
                    type: z.literal(ExperienceType.Commercial),
                    details: z.object({
                        subType: z.nativeEnum(CommercialType),
                    }),
                }),
                z.object({
                    type: z.literal(ExperienceType.Activity),
                    details: z.object({
                        subType: z.nativeEnum(ActivityType),
                    }),
                }),
                z.object({
                    type: z.literal(ExperienceType.Boat),
                    details: z.object({
                        subType: z.nativeEnum(BoatType),
                    }),
                }),
            ]),
        ),
    });

    const { handleSubmit, setValue, control, reset, resetField } = methods;

    const [createExperience] = useCreateExperienceMutation();

    const type = useWatch({ control, name: 'type' });

    const handleContinue = async (data: CreateExperience) => {
        try {
            const response = await createExperience({
                ownerId: loggedInUser?.company,
                experience: data,
            }).unwrap();

            navigate(`/experiences/new/${response.id}`);
        } catch (_error) {
            toast.error('Failed to create experience');
        }
    };

    const handleCancel = () => {
        onClose();
        reset();
    };

    const subTypeOptions = [
        ...(type === ExperienceType.Property
            ? [
                  { value: 'apartment', label: 'Apartment' },
                  { value: 'cabin', label: 'Cabin' },
                  { value: 'duplex', label: 'Duplex' },
                  { value: 'house', label: 'House' },
                  { value: 'unit', label: 'Unit' },
              ]
            : type === ExperienceType.Vehicle
              ? [
                    { value: 'car', label: 'Car' },
                    { value: 'motorbike', label: 'Motorbike' },
                ]
              : type === ExperienceType.Commercial
                ? [
                      { value: 'office', label: 'Office Space' },
                      { value: 'house', label: 'Home Office' },
                  ]
                : type === ExperienceType.Activity
                  ? [
                        { value: 'buggies', label: 'Buggies' },
                        { value: 'eBikes', label: 'E-Bikes' },
                        { value: 'goKarts', label: 'GoKarts' },
                    ]
                  : type === ExperienceType.Boat
                    ? [
                          { value: 'bass', label: 'Bass Boat' },
                          {
                              value: 'cabin cruiser',
                              label: 'Cabin Cruiser',
                          },
                          { value: 'canoe', label: 'Canoe' },
                          { value: 'catamaran', label: 'Catamaran Boat' },
                          { value: 'dingy', label: 'Dingy' },
                          { value: 'jetboat', label: 'Jetboat' },
                          { value: 'runabout', label: 'Runabout Boat' },
                          {
                              value: 'inflatable',
                              label: 'Inflatable Boat',
                          },
                          { value: 'sailboat', label: 'Sailing Boat' },
                          { value: 'skiff', label: 'Skiff Boat' },
                          { value: 'superyacht', label: 'Superyacht' },
                          { value: 'yacht', label: 'Yacht' },
                      ]
                    : []),
    ];

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth='sm' fullWidth>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(handleContinue)}>
                    <DialogTitle>New Listing</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <div>
                                <Typography
                                    gutterBottom
                                    id='experience-type-select'
                                >
                                    What type of experience would you like to
                                    create?
                                </Typography>
                                <Grid
                                    container
                                    spacing={1}
                                    columns={10}
                                    aria-labelledby='experience-type-select'
                                >
                                    {MODULE_TYPE_CHIPS.map((experienceType) => (
                                        <Grid
                                            key={experienceType.value}
                                            item
                                            xs={10}
                                            md={2}
                                        >
                                            <Card
                                                variant='outlined'
                                                sx={{
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                    borderColor:
                                                        type ===
                                                        experienceType.value
                                                            ? 'primary.main'
                                                            : 'divider',
                                                    bgcolor:
                                                        type ===
                                                        experienceType.value
                                                            ? 'action.selected'
                                                            : 'background.paper',
                                                }}
                                                onClick={() => {
                                                    resetField(
                                                        'details.subType',
                                                    );
                                                    setValue(
                                                        'type',
                                                        experienceType.value as ExperienceType,
                                                    );
                                                }}
                                            >
                                                <CardContent
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    <Typography>
                                                        {experienceType.label}
                                                    </Typography>
                                                    {experienceType.icon}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                            <Fade in={!!type} key={type}>
                                <div>
                                    <Typography
                                        id='subtype-select'
                                        gutterBottom
                                    >
                                        Which best describes your{' '}
                                        {type === ExperienceType.Commercial
                                            ? 'commercial space'
                                            : type}
                                        ?
                                    </Typography>
                                    <FormSelect
                                        aria-labelledby='subtype-select'
                                        name='details.subType'
                                        control={control}
                                        options={subTypeOptions}
                                    />
                                </div>
                            </Fade>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant='contained'
                            color='error'
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <Button variant='contained' type='submit'>
                            Continue
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ListingModal;
